import { DateRange, DateRangePicker, LocalizationProvider } from '@material-ui/pickers';
import DateFnsUtils from '@material-ui/pickers/adapter/date-fns';

import React, { FC, useCallback, useEffect, useState } from 'react';

import { createPortal } from 'react-dom';

import { Box, Button, IconButton, InputAdornment, TextField, useMediaQuery, useTheme } from '@material-ui/core';

import { DateRangeOutlined } from '@material-ui/icons';

import { endOfDay, endOfMonth, endOfWeek, isSameDay, startOfDay, startOfMonth, startOfWeek } from 'date-fns';

import { getLocale } from 'src/utils';

import { ReactComponent as Search } from '../../assets/Common/Search.svg';

import { useStyles } from './styles';
import { useAppData } from '../../providers';

interface Props {
  defaultValue: DateRange<Date>;
  minDate?: Date;
  onRangeChange: (dateRange: DateRange<Date>) => void;
}

const parseTranslations = (translations?: string): Record<string, string> => {
  if (!translations) {
    return {};
  }

  try {
    return JSON.parse(translations);
  } catch {
    return {};
  }
};

const createShortcutsMountElement = () => {
  const element = document.createElement('div');

  element.setAttribute('data-date-range-shortcuts', 'true');

  element.style.width = '100%';
  element.style.margin = '0';
  element.style.padding = '0';
  element.style.flexShrink = '0';
  element.style.boxSizing = 'border-box';

  return element;
};

export const DateRangeSelect: FC<Props> = ({ defaultValue, onRangeChange, minDate }) => {
  const classes = useStyles();

  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { locale, translations } = useAppData();

  const [value, setValue] = useState<DateRange<Date>>(defaultValue);

  const [localeString, setLocaleString] = useState('en');

  const [open, setOpen] = useState(false);

  const [translation, setTranslation] = useState<Record<string, string>>({});

  const [shortcutsContainer, setShortcutsContainer] = useState<HTMLElement | null>(null);

  const showSearchButton = Boolean(
    isDesktop &&
      value[0] &&
      value[1] &&
      isSameDay(value[0], value[1]),
  );

  useEffect(() => {
    if (locale) {
      setLocaleString(locale);
    }
  }, [locale]);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    setTranslation(parseTranslations(translations));
  }, [translations]);

  useEffect(() => {
    if (!open) {
      setShortcutsContainer(null);

      return undefined;
    }

    let mountElement: HTMLDivElement | null = null;

    let observer: MutationObserver | null = null;

    const mountShortcuts = () => {
      if (mountElement?.parentElement) {
        return true;
      }

      const pickerPaper = Array.from(
        document.querySelectorAll<HTMLElement>(
          '.MuiPickersPopper-paper, .MuiDialog-paper',
        ),
      )
        .reverse()
        .find((element) =>
          element.querySelector(
            [
              '.MuiPickersDesktopDateRangeCalendar-root',
              '.MuiPickersDesktopDateRangeCalendar-rangeCalendarContainer',
              '.MuiPickersBasePicker-pickerView',
              '.MuiPickersCalendar-root',
            ].join(','),
          ),
        );

      if (!pickerPaper) {
        return false;
      }

      const dialogActions =
        pickerPaper.querySelector<HTMLElement>(
          '.MuiPickersModalDialog-dialogAction',
        ) ||
        pickerPaper.querySelector<HTMLElement>(
          '.MuiPickersModal-dialogAction',
        ) ||
        pickerPaper.querySelector<HTMLElement>(
          '.MuiDialogActions-root',
        );

      const desktopCalendar =
        pickerPaper.querySelector<HTMLElement>(
          '.MuiPickersDesktopDateRangeCalendar-root',
        ) ||
        pickerPaper.querySelector<HTMLElement>(
          '.MuiPickersDesktopDateRangeCalendar-rangeCalendarContainer',
        );

      const pickerView = pickerPaper.querySelector<HTMLElement>(
        '.MuiPickersBasePicker-pickerView',
      );

      mountElement = createShortcutsMountElement();

      if (dialogActions && dialogActions.parentElement) {
        mountElement.setAttribute(
          'data-date-range-shortcuts-view',
          'mobile',
        );

        dialogActions.parentElement.insertBefore(
          mountElement,
          dialogActions,
        );
      } else if (desktopCalendar && desktopCalendar.parentElement) {
        mountElement.setAttribute(
          'data-date-range-shortcuts-view',
          'desktop',
        );

        desktopCalendar.parentElement.insertBefore(
          mountElement,
          desktopCalendar.nextSibling,
        );
      } else if (pickerView && pickerView.parentElement) {
        mountElement.setAttribute(
          'data-date-range-shortcuts-view',
          'mobile',
        );

        pickerView.parentElement.insertBefore(
          mountElement,
          pickerView.nextSibling,
        );
      } else {
        mountElement.setAttribute(
          'data-date-range-shortcuts-view',
          'mobile',
        );

        pickerPaper.appendChild(mountElement);
      }

      setShortcutsContainer(mountElement);

      return true;
    };

    if (!mountShortcuts()) {
      observer = new MutationObserver(() => {
        if (mountShortcuts()) {
          observer?.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer?.disconnect();

      setShortcutsContainer(null);

      if (mountElement?.parentElement) {
        mountElement.parentElement.removeChild(mountElement);
      }
    };
  }, [open]);

  const applyRange = useCallback(
    (range: DateRange<Date>) => {
      setValue(range);
      onRangeChange(range);
      setOpen(false);
    },
    [onRangeChange],
  );

  const handleToday = () => {
    const today = new Date();

    applyRange([startOfDay(today), endOfDay(today)]);
  };

  const handleThisWeek = () => {
    const today = new Date();

    applyRange([
      startOfWeek(today, {
        weekStartsOn: 1,
      }),
      endOfWeek(today, {
        weekStartsOn: 1,
      }),
    ]);
  };

  const handleThisMonth = () => {
    const today = new Date();

    applyRange([
      startOfMonth(today),
      endOfMonth(today),
    ]);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleIconClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setOpen((previousOpen) => !previousOpen);
  };

  const handleSearch = useCallback(() => {
    const [from, to] = value;

    if (!from || !to) {
      return;
    }

    onRangeChange(value);
  }, [value, onRangeChange]);

  const shortcuts = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
      gridGap={5}
      px={2}
      py={2}
      width="100%"
      boxSizing="border-box"
    >
      <Button
        size="small"
        variant="outlined"
        onClick={handleToday}
      >
        {translation.text_273 || 'Today'}
      </Button>

      <Button
        size="small"
        variant="outlined"
        onClick={handleThisWeek}
      >
        {translation.text_834 || 'This week'}
      </Button>

      <Button
        size="small"
        variant="outlined"
        onClick={handleThisMonth}
      >
        {translation.text_836 || 'This month'}
      </Button>
    </Box>
  );

  return (
    <LocalizationProvider
      dateAdapter={DateFnsUtils}
      locale={getLocale(localeString)}
    >
      <>
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          gridGap={8}
        >
          <Box
            flex={1}
            minWidth={0}
            style={{
              border: '1px solid rgba(3, 12, 33, 0.23)',
              borderRadius: 8,
            }}
          >
            <DateRangePicker
              showToolbar={false}
              open={open}
              calendars={1}
              value={value}
              minDate={minDate}
              onChange={(newValue: DateRange<Date>) => {
                setValue(newValue);
              }}
              onAccept={(newValue: unknown) => {
                if (
                  !Array.isArray(newValue) ||
                  newValue.length !== 2
                ) {
                  return;
                }

                const [from, to] = newValue;

                if (
                  !(from instanceof Date) ||
                  !(to instanceof Date)
                ) {
                  return;
                }

                const range: DateRange<Date> = [from, to];

                setValue(range);
                onRangeChange(range);
                setOpen(false);
              }}
              onClose={() => {
                setOpen(false);
              }}
              InputProps={{
                margin: 'dense',
              }}
              renderInput={(startProps, endProps) => {
                const {
                  value: startValue,
                  ...restStartProps
                } = startProps.inputProps || {};

                const endValue =
                  endProps?.inputProps?.value;

                return (
                  <TextField
                    {...startProps}
                    fullWidth
                    helperText={null}
                    label={null}
                    className={classes.root}
                    onClick={handleOpen}
                    value={`${
                      startValue ||
                      translation
                        .Reports_PeriodPartnershipLoginFilter_Period_From ||
                      'From'
                    } - ${
                      endValue ||
                      translation
                        .Reports_PeriodPartnershipLoginFilter_Period_To ||
                      'To'
                    }`}
                    InputProps={{
                      ...startProps.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleIconClick}
                          >
                            <DateRangeOutlined
                              style={{
                                color: '#030C21',
                              }}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...restStartProps,
                        readOnly: true,
                      },
                    }}
                  />
                );
              }}
            />
          </Box>

          {showSearchButton && (
            <IconButton
              onClick={handleSearch}
              aria-label="Search by selected date range"
              style={{
                width: 48,
                height: 48,
                flexShrink: 0,
                padding: 0,
                border: 'none',
                background: 'transparent',
              }}
            >
              <Search />
            </IconButton>
          )}
        </Box>

        {open &&
          shortcutsContainer &&
          createPortal(
            shortcuts,
            shortcutsContainer,
          )}
      </>
    </LocalizationProvider>
  );
};
